import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef
} from "react";
import { GradientValue } from "lib/gradients/gradientTypes";

type GradientTargetType = "pageBackground" | "element" | null;

interface GradientEditingState {
  isActive: boolean;
  gradient: GradientValue | null;
  onGradientChange: ((gradient: GradientValue) => void) | null;
  selectedStopId: string | null;
  onSelectStop: ((stopId: string) => void) | null;
  /** Identifies which GradientPicker instance owns this editing session. */
  sessionId: string | null;
  targetType: GradientTargetType;
  elementBounds: { width: number; height: number } | null;
  elementId: string | null;
}

interface GradientEditingContextValue extends GradientEditingState {
  startGradientEditing: (
    sessionId: string,
    gradient: GradientValue,
    onChange: (gradient: GradientValue) => void,
    selectedStopId: string | null,
    onSelectStop: (stopId: string) => void,
    targetType?: "pageBackground" | "element",
    elementBounds?: { width: number; height: number },
    elementId?: string
  ) => void;
  updateGradientEditing: (
    sessionId: string,
    gradient: GradientValue,
    selectedStopId?: string | null
  ) => void;
  stopGradientEditing: (sessionId: string) => void;
}

const defaultContext: GradientEditingContextValue = {
  isActive: false,
  gradient: null,
  onGradientChange: null,
  selectedStopId: null,
  onSelectStop: null,
  sessionId: null,
  targetType: null,
  elementBounds: null,
  elementId: null,
  startGradientEditing: () => {},
  updateGradientEditing: () => {},
  stopGradientEditing: () => {}
};

export const GradientEditingContext =
  createContext<GradientEditingContextValue>(defaultContext);

export const useGradientEditing = () => useContext(GradientEditingContext);

interface GradientEditingProviderProps {
  children: React.ReactNode;
}

export const GradientEditingProvider = ({
  children
}: GradientEditingProviderProps) => {
  const [state, setState] = useState<GradientEditingState>({
    isActive: false,
    gradient: null,
    onGradientChange: null,
    selectedStopId: null,
    onSelectStop: null,
    sessionId: null,
    targetType: null,
    elementBounds: null,
    elementId: null
  });

  // Refs ensure the latest callbacks are always available, avoiding stale
  // closures during rapid drag updates where React state may lag.
  const onChangeRef = useRef<((gradient: GradientValue) => void) | null>(null);
  const onSelectStopRef = useRef<((stopId: string) => void) | null>(null);

  // Stable wrapper functions that always delegate to the latest ref callback.
  // These never change identity, so consumers don't need to track them in deps.
  const stableOnChange = useCallback((gradient: GradientValue) => {
    onChangeRef.current?.(gradient);
  }, []);

  const stableOnSelectStop = useCallback((stopId: string) => {
    onSelectStopRef.current?.(stopId);
  }, []);

  const startGradientEditing = useCallback(
    (
      sessionId: string,
      gradient: GradientValue,
      onChange: (gradient: GradientValue) => void,
      selectedStopId: string | null,
      onSelectStop: (stopId: string) => void,
      targetType?: "pageBackground" | "element",
      elementBounds?: { width: number; height: number },
      elementId?: string
    ) => {
      setState(prev => {
        // If another session already owns editing, reject this start.
        // First caller wins — prevents a hidden duplicate instance from
        // stealing ownership from the visible one.
        if (
          prev.isActive &&
          prev.sessionId !== null &&
          prev.sessionId !== sessionId
        ) {
          return prev;
        }
        onChangeRef.current = onChange;
        onSelectStopRef.current = onSelectStop;
        return {
          isActive: true,
          gradient,
          onGradientChange: stableOnChange,
          selectedStopId,
          onSelectStop: stableOnSelectStop,
          sessionId,
          targetType: targetType ?? null,
          elementBounds: elementBounds ?? null,
          elementId: elementId ?? null
        };
      });
    },
    [stableOnChange, stableOnSelectStop]
  );

  const updateGradientEditing = useCallback(
    (
      sessionId: string,
      gradient: GradientValue,
      selectedStopId?: string | null
    ) => {
      setState(prev => {
        // Only the owning session can push updates
        if (!prev.isActive || prev.sessionId !== sessionId) return prev;
        const updates: Partial<GradientEditingState> = { gradient };
        if (selectedStopId !== undefined) {
          updates.selectedStopId = selectedStopId;
        }
        return { ...prev, ...updates };
      });
    },
    []
  );

  const stopGradientEditing = useCallback((sessionId: string) => {
    onChangeRef.current = null;
    onSelectStopRef.current = null;
    setState(prev => {
      // Only the owning session can stop editing
      if (prev.sessionId !== null && prev.sessionId !== sessionId) return prev;
      return {
        isActive: false,
        gradient: null,
        onGradientChange: null,
        selectedStopId: null,
        onSelectStop: null,
        sessionId: null,
        targetType: null,
        elementBounds: null,
        elementId: null
      };
    });
  }, []);

  const value = useMemo<GradientEditingContextValue>(
    () => ({
      ...state,
      startGradientEditing,
      updateGradientEditing,
      stopGradientEditing
    }),
    [state, startGradientEditing, updateGradientEditing, stopGradientEditing]
  );

  return (
    <GradientEditingContext.Provider value={value}>
      {children}
    </GradientEditingContext.Provider>
  );
};
